import { useState } from "react";
import { api, formatErr } from "../lib/api";
import { toast } from "sonner";
import { CreditCard, ShieldCheck } from "lucide-react";

const POSITIONS = ["Guard", "Point Guard", "Shooting Guard", "Forward", "Small Forward", "Power Forward", "Center"];

export default function Tryouts() {
    const [form, setForm] = useState({
        player_name: "", dob: "", age: "", height: "", position: "Guard",
        phone: "", email: "", notes: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [tryoutId, setTryoutId] = useState(null);
    const [redirecting, setRedirecting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = { ...form, age: Number(form.age) };
            const { data } = await api.post("/tryouts", payload);
            setTryoutId(data.id);
            toast.success("Application received — proceed to payment to lock your spot.");
        } catch (err) {
            toast.error(formatErr(err));
        } finally { setSubmitting(false); }
    };

    const pay = async () => {
        if (!tryoutId) return;
        setRedirecting(true);
        try {
            const { data } = await api.post("/payments/checkout", {
                tryout_id: tryoutId,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (err) {
            toast.error(formatErr(err));
            setRedirecting(false);
        }
    };

    return (
        <div data-testid="tryouts-page" className="grain">
            <section className="border-b border-line py-16 md:py-24 bg-bg-soft">
                <div className="max-w-7xl mx-auto px-5 md:px-10">
                    <div className="text-gold text-xs tracking-[0.4em] uppercase font-bold mb-4">Open Tryouts</div>
                    <h1 className="h-display text-6xl md:text-8xl">Wear The Crown</h1>
                    <p className="text-ink text-lg mt-5 max-w-2xl">
                        Open to athletes 18+. Submit your registration, complete payment, and we'll send you tryout details.
                    </p>
                </div>
            </section>

            <section className="max-w-5xl mx-auto px-5 md:px-10 py-16 grid md:grid-cols-5 gap-10">
                <div className="md:col-span-3">
                    {!tryoutId ? (
                        <form onSubmit={submit} className="space-y-5" data-testid="tryouts-form">
                            <div className="grid sm:grid-cols-2 gap-4">
                                <Field testid="ty-name" label="Player Name" required>
                                    <input required value={form.player_name} onChange={(e) => setForm({ ...form, player_name: e.target.value })} className="w-full px-4 py-3" />
                                </Field>
                                <Field testid="ty-email" label="Email" required>
                                    <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3" />
                                </Field>
                                <Field testid="ty-phone" label="Phone" required>
                                    <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-4 py-3" />
                                </Field>
                                <Field testid="ty-dob" label="Date of Birth" required>
                                    <input required type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} className="w-full px-4 py-3" />
                                </Field>
                                <Field testid="ty-age" label="Age" required>
                                    <input required type="number" min="18" max="50" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="w-full px-4 py-3" />
                                </Field>
                                <Field testid="ty-height" label="Height (ft' in)" required>
                                    <input required placeholder="6'2&quot;" value={form.height} onChange={(e) => setForm({ ...form, height: e.target.value })} className="w-full px-4 py-3" />
                                </Field>
                                <Field testid="ty-position" label="Position" required>
                                    <select value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className="w-full px-4 py-3">
                                        {POSITIONS.map((p) => <option key={p}>{p}</option>)}
                                    </select>
                                </Field>
                            </div>
                            <Field testid="ty-notes" label="Notes (Optional)">
                                <textarea rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-4 py-3" placeholder="Tell us about your hoops resume — leagues played, accomplishments, references." />
                            </Field>
                            <button type="submit" disabled={submitting} data-testid="ty-submit" className="btn-gold px-7 py-4 text-xs disabled:opacity-60 inline-flex items-center gap-2">
                                <ShieldCheck size={16} /> {submitting ? "Submitting…" : "Submit Application"}
                            </button>
                        </form>
                    ) : (
                        <div className="card-flat p-8 text-center" data-testid="ty-payment-step">
                            <ShieldCheck className="mx-auto text-gold" size={40} />
                            <h3 className="h-display text-3xl mt-5">One Step Left</h3>
                            <p className="text-ink mt-3">Pay the registration fee to confirm your spot. You'll receive an email with tryout details after payment.</p>
                            <button onClick={pay} disabled={redirecting} data-testid="ty-pay-btn" className="btn-gold px-8 py-4 mt-6 inline-flex items-center gap-2 text-xs">
                                <CreditCard size={16} /> {redirecting ? "Redirecting…" : "Pay $50 Registration Fee"}
                            </button>
                        </div>
                    )}
                </div>

                <aside className="md:col-span-2 space-y-4">
                    <div className="card-flat p-6">
                        <div className="text-gold text-[10px] tracking-[0.3em] uppercase font-bold mb-3">What you get</div>
                        <ul className="space-y-2 text-sm text-white/80">
                            <li>· On-court evaluation by Purple Kings coaches</li>
                            <li>· Skills sessions: shooting, defense, scrimmages</li>
                            <li>· Feedback from staff</li>
                            <li>· Spot consideration for 2026-27 roster</li>
                        </ul>
                    </div>
                    <div className="card-flat p-6">
                        <div className="text-gold text-[10px] tracking-[0.3em] uppercase font-bold mb-3">Fee</div>
                        <div className="h-display text-4xl text-gold">$50</div>
                        <p className="text-ink text-xs mt-2">Non-refundable. Secured by Stripe.</p>
                    </div>
                </aside>
            </section>
        </div>
    );
}

function Field({ label, required, children, testid }) {
    return (
        <label className="block" data-testid={`field-${testid}`}>
            <div className="text-[10px] tracking-[0.3em] uppercase text-ink font-bold mb-2">
                {label} {required && <span className="text-gold">*</span>}
            </div>
            {children}
        </label>
    );
}
